import { ElementRef, EventEmitter, Injectable } from '@angular/core';
import { PanZoomStatus } from './pan-zoom-staus.model';
import { PanZoomActionEnum } from './pan-zoom-action.enum';
import { getDistance } from '@lib/core';

// Definiujemy interfejsy dla czytelności kodu
interface PanZoomElements {
  viewerContainer: ElementRef<HTMLDivElement>;
  mainView: ElementRef<HTMLDivElement>;
  mainContent: ElementRef<HTMLDivElement>;
  viewportIndicator: ElementRef<HTMLDivElement>;
  miniView: ElementRef<HTMLDivElement>;
  miniContent: ElementRef<HTMLDivElement>;
}

interface PanZoomConfig {
  zoomLevel: number;
  maxZoomLevel: number;
  minZoomLevel: number;
  scrollOffset: number;
  maxMiniatureWidth: number;
}

@Injectable()
export class PanZoomService {
  private elements!: PanZoomElements;
  private config!: PanZoomConfig;
  private zoomStatusChange!: EventEmitter<PanZoomStatus>;

  private zoomRatio = 1;
  private miniatureZoomRatio = 1;
  private viewportUpdateTimeout: any;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartScrollLeft = 0;
  private touchStartScrollTop = 0;
  private initialDistance = 0;
  private initialZoomLevel = 1;
  private isPinching = false;
  private miniatureScale = 1;

  public zoomLevel!: number;
  public isZoomInDisabled = false;
  public isZoomOutDisabled = true;
  public showTopChevron = false;
  public showBottomChevron = false;
  public showLeftChevron = false;
  public showRightChevron = false;
  public miniatureRendered = false;

  public init(elements: PanZoomElements, config: PanZoomConfig, zoomStatusChange: EventEmitter<PanZoomStatus>): void {
    this.elements = elements;
    this.config = config;
    this.zoomStatusChange = zoomStatusChange;
    this.zoomLevel = this.config.zoomLevel;

    this.applyZoom();
    this.initViewport();
    this.setupTouchEvents();

    this.viewportUpdateTimeout = setTimeout(() => {
      this.updateViewportIndicator();
    }, 400);
  }

  public destroy(): void {
    clearTimeout(this.viewportUpdateTimeout);
  }

  private setupTouchEvents(): void {
    const mainContent = this.elements.mainContent.nativeElement;
    mainContent.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    mainContent.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    mainContent.addEventListener('touchend', this.handleTouchEnd.bind(this));
  }

  private handleTouchStart(event: TouchEvent): void {
    if (event.touches.length === 1) {
      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchStartScrollLeft = this.elements.mainView.nativeElement.scrollLeft;
      this.touchStartScrollTop = this.elements.mainView.nativeElement.scrollTop;
      this.isPinching = false;
    } else if (event.touches.length === 2) {
      this.isPinching = true;
      this.initialDistance = getDistance(event);
      this.initialZoomLevel = this.zoomLevel;
      event.preventDefault();
    }
  }

  private handleTouchMove(event: TouchEvent): void {
    if (event.touches.length === 1 && !this.isPinching) {
      const touch = event.touches[0];
      const dx = touch.clientX - this.touchStartX;
      const dy = touch.clientY - this.touchStartY;
      this.elements.mainView.nativeElement.scrollLeft = this.touchStartScrollLeft - dx;
      this.elements.mainView.nativeElement.scrollTop = this.touchStartScrollTop - dy;
      event.preventDefault();
      this.updateViewportIndicator();
    } else if (event.touches.length === 2 && this.isPinching) {
      event.preventDefault();
      const currentDistance = getDistance(event);
      this.zoom(currentDistance < this.initialDistance ? 'out' : 'in');
    }
  }

  private handleTouchEnd(): void {
    if (this.isPinching) {
      this.isPinching = false;
      this.updateZoomButtonsState();
    }

    this.updateViewportIndicator();
  }

  public move(direction: 'up' | 'down' | 'left' | 'right'): void {
    const mainView = this.elements.mainView.nativeElement;
    const scrollOffset = this.config.scrollOffset;
    switch (direction) {
      case 'up':
        mainView.scrollTop -= mainView.clientHeight + scrollOffset;
        break;
      case 'down':
        mainView.scrollTop += mainView.clientHeight - scrollOffset;
        break;
      case 'left':
        mainView.scrollLeft -= mainView.clientWidth + scrollOffset;
        break;
      case 'right':
        mainView.scrollLeft += mainView.clientWidth - scrollOffset;
        break;
    }
    this.updateViewportIndicator();
  }

  public zoom(inOut: 'in' | 'out'): void {
    const mainView = this.elements.mainView.nativeElement;
    const mainContent = this.elements.mainContent.nativeElement;

    const oldScale = Math.max(this.zoomLevel * this.zoomRatio, 1);
    const oldScrollLeft = mainView.scrollLeft;
    const oldScrollTop = mainView.scrollTop;

    if (mainView.clientWidth < 576) {
      this.zoomRatio = 0;
      this.miniatureZoomRatio = mainView.scrollWidth != 0 ? mainView.scrollHeight / mainView.scrollWidth : 1;
    } else {
      this.zoomRatio = 1;
      this.miniatureZoomRatio = 1;
    }

    if (inOut === 'in' && this.zoomLevel < this.config.maxZoomLevel) {
      this.zoomLevel += 1;
      this.zoomStatusChange.emit({ action: PanZoomActionEnum.ZOOM_IN, level: this.zoomLevel });
    } else if (inOut === 'out' && this.zoomLevel > this.config.minZoomLevel) {
      this.zoomLevel -= 1;
      this.zoomStatusChange.emit({ action: PanZoomActionEnum.ZOOM_OUT, level: this.zoomLevel });
    }

    this.updateZoomButtonsState();
    const newScale = Math.max(this.zoomLevel * this.zoomRatio, 1);
    setTimeout(() => {
      mainContent.style.transform = `scale(${newScale})`;

      if (oldScale !== newScale) {
        const centerX = oldScrollLeft + mainView.clientWidth / 2;
        const centerY = oldScrollTop + mainView.clientHeight / 2;
        const newScrollLeft = centerX * (newScale / oldScale) - mainView.clientWidth / 2;
        const newScrollTop = centerY * (newScale / oldScale) - mainView.clientHeight / 2;
        mainView.scrollLeft = newScrollLeft;
        mainView.scrollTop = newScrollTop;
      }

      clearTimeout(this.viewportUpdateTimeout);
      this.viewportUpdateTimeout = setTimeout(() => this.updateViewportIndicator(), 400);
    }, 100);
  }

  private applyZoom(): void {
    const scale = Math.max(this.zoomLevel * this.zoomRatio, 1);
    this.elements.mainContent.nativeElement.style.transform = `scale(${scale})`;
    this.updateZoomButtonsState();
  }

  public updateOnScroll(): void {
    this.updateViewportIndicator();
  }

  private updateZoomButtonsState(): void {
    this.isZoomInDisabled = this.zoomLevel >= this.config.maxZoomLevel;
    this.isZoomOutDisabled = this.zoomLevel <= this.config.minZoomLevel;
  }

  public updateViewportIndicator(): void {
    const mainView = this.elements.mainView.nativeElement;

    this.showTopChevron = mainView.scrollTop > 0;
    this.showBottomChevron = mainView.scrollTop + mainView.clientHeight < mainView.scrollHeight - 1; // -1 for precision
    this.showLeftChevron = mainView.scrollLeft > 0;
    this.showRightChevron = mainView.scrollLeft + mainView.clientWidth < mainView.scrollWidth - 1; // -1 for precision

    if (!this.miniatureScale || this.zoomLevel === 0 || this.miniatureZoomRatio === 0) {
      return;
    }

    //console.log(this.miniatureScale, this.zoomLevel, this.miniatureZoomRatio, this.zoomRatio);
    const scale = this.zoomRatio == 0 ? this.miniatureScale : this.miniatureScale / this.zoomLevel / this.miniatureZoomRatio;
    const indicator = this.elements.viewportIndicator.nativeElement;
    if (indicator) {
      indicator.style.width = `${mainView.clientWidth * scale}px`;
      indicator.style.height = `${mainView.clientHeight * scale}px`;
      indicator.style.left = `${mainView.scrollLeft * scale}px`;
      indicator.style.top = `${mainView.scrollTop * scale}px`;
    }
  }

  public initViewport(): void {
    const mainView = this.elements.mainView.nativeElement;
    const miniContent = this.elements.miniContent.nativeElement;
    const mainViewAspectRatio = mainView.scrollWidth / mainView.scrollHeight;

    const miniatureWidth = this.config.maxMiniatureWidth;
    const miniatureHeight = miniatureWidth / mainViewAspectRatio;

    const miniView = this.elements.miniView.nativeElement;
    miniView.style.width = `${miniatureWidth}px`;
    miniView.style.height = `${miniatureHeight}px`;

    const section = miniContent.querySelector('section');
    if (section) {
      section.style.width = `${mainView.scrollWidth}px`;
      section.style.height = `${mainView.scrollHeight}px`;
    }

    const scale = Math.min(miniatureWidth / mainView.scrollWidth, miniatureHeight / mainView.scrollHeight);
    miniContent.style.transform = `scale(${scale})`;
    miniContent.style.transformOrigin = '0 0';
    miniContent.style.width = `${mainView.scrollWidth}px`;

    this.miniatureScale = scale;
    this.miniatureRendered = true;
  }
}
